// Visible neutral for "Others" / unmapped — the dataset uses #F5F5F5 (invisible on white).
export const OTHERS_COLOR = '#9aa0a6';

const visible = (hex) => {
  if (!hex) return OTHERS_COLOR;
  return hex.toLowerCase() === '#f5f5f5' ? OTHERS_COLOR : hex;
};

// Build a party -> coalition map for a given election entry.
// Returns { partyToCoalition: { [party]: coalitionName }, colorOf: (key) => hex }
export const buildCoalitionLookup = (election) => {
  const partyToCoalition = {};
  Object.entries(election.coalitions || {}).forEach(([coalition, parties]) => {
    parties.forEach((p) => {
      partyToCoalition[p] = coalition;
    });
  });
  const colors = election.coalitionColors || {};
  const colorOf = (key) => {
    if (colors[key]) return visible(colors[key]);
    const c = partyToCoalition[key];
    if (c && colors[c]) return visible(colors[c]);
    return OTHERS_COLOR;
  };
  return { partyToCoalition, colorOf };
};

// Aggregate seats per coalition for a given year, and per-party seats grouped under coalition.
// Returns:
// [{ coalition, color, seats, parties: [{ party, seats }] }] sorted desc by seats.
export const aggregateByCoalition = (election) => {
  const { partyToCoalition } = buildCoalitionLookup(election);
  const colors = election.coalitionColors || {};
  const partyTotals = {};

  // Sum from states (always available, ground truth).
  (election.states || []).forEach((state) => {
    Object.entries(state.breakdown || {}).forEach(([party, seats]) => {
      partyTotals[party] = (partyTotals[party] || 0) + seats;
    });
  });

  // Group parties by coalition.
  const groups = {};
  Object.entries(partyTotals).forEach(([party, seats]) => {
    const coalition = partyToCoalition[party] || 'Others';
    if (!groups[coalition]) {
      groups[coalition] = { coalition, color: visible(colors[coalition]), seats: 0, parties: [] };
    }
    groups[coalition].seats += seats;
    groups[coalition].parties.push({ party, seats });
  });

  // National totals may include coalition-level direct entries (e.g., "Alliance: 51")
  // for early elections where the breakdown only knows the coalition. Patch those in.
  Object.entries(election.nationalTotals || {}).forEach(([key, seats]) => {
    if (election.coalitions[key] && !groups[key]) {
      groups[key] = { coalition: key, color: visible(colors[key]), seats, parties: [] };
    } else if (election.coalitions[key] && groups[key].seats === 0) {
      groups[key].seats = seats;
    }
  });

  return Object.values(groups)
    .map((g) => ({ ...g, parties: g.parties.sort((a, b) => b.seats - a.seats) }))
    .sort((a, b) => b.seats - a.seats);
};
